/**
 * Backend base URL and a fetch helper.
 *
 * The backend URL always comes from the environment — hardcoding it breaks auth,
 * because the session cookie is bound to the origin that set it.
 */
export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

const GUEST_ID_KEY = "fuelly_guest_id";

/**
 * Absolute URL for something the backend serves (dish photos today).
 *
 * The API hands back a path relative to its own origin ("/media/…"), which would
 * 404 against the dev server if rendered bare. Anything already absolute (a CDN,
 * once photos move off the box) is passed through untouched.
 */
export function mediaUrl(url) {
  if (!url) return null;
  if (/^(https?:)?\/\//i.test(url) || url.startsWith("data:")) return url;
  return `${BACKEND}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * A stable per-device id, sent on every request.
 *
 * This is only a rate-limiting key, never an identity: everyone in the restaurant
 * shares its Wi-Fi, so without it the server can only tell guests apart by IP and
 * one busy lunch looks exactly like one person hammering the API.
 */
export function guestId() {
  try {
    let id = localStorage.getItem(GUEST_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `g-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(GUEST_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return "anonymous"; // private browsing with storage disabled
  }
}

export class ApiError extends Error {
  constructor(status, detail) {
    // No status means the request never got an answer — a network failure or a
    // timeout — which serverMessages.js reads as "offline".
    super(detail || (status ? `Request failed (${status})` : "Network request failed"));
    this.status = status;
    this.detail = detail;
  }
}

// Generous on purpose: an AI plate legitimately takes the model's time. This
// exists for the phone that walked out of Wi-Fi range mid-request, where fetch
// would otherwise hang the screen for minutes.
export const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * GET/POST JSON against the API, always sending the session cookie.
 *
 * Every failure comes out as ApiError: an HTTP status carries the server's
 * detail, a network failure or timeout carries no status at all. Callers never
 * see a raw TypeError, so one catch handles both worlds.
 */
export async function fetchJson(path, options = {}) {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...rest } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetch(`${API}${path}`, {
      credentials: "include",
      ...rest,
      // After the spread, so a caller's `headers` adds to these instead of
      // silently dropping X-Guest-Id — losing it merges the whole room's
      // rate-limit budget into one bucket.
      signal: rest.signal ?? controller.signal,
      headers: {
        "Content-Type": "application/json",
        "X-Guest-Id": guestId(),
        ...(rest.headers || {}),
      },
    });
  } catch (e) {
    throw new ApiError(undefined, null);
  } finally {
    clearTimeout(timer);
  }

  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    // empty or non-JSON body
  }
  if (!res.ok) throw new ApiError(res.status, body?.detail);
  return body;
}
